import React, { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Tooltip, Icon, Text, Spacer } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';
import { MAX_IMAGE_SIZE_COMPANY_LOGO_IN_MB } from '../../../../../constants/Environment';
import { Logo } from './Logo';
import CropImageDialog from '../dialogs/CropImageDialog';
import './styles.scss';

const propTypes = {
    field: PropTypes.object.isRequired,
    type: PropTypes.object,
    src: PropTypes.string,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    tooltip: PropTypes.string,
    onChange: PropTypes.func,
};

const CompanyLogo = memo(({ field, type, src, description, title, tooltip, onChange }) => {
    const [showCropImageDialog, setShowCropImageDialog] = useState(false);
    const [newFile, setNewFile] = useState(null);

    const onNewImage = useCallback((file) => {
        if (file.size / 1024 / 1024 > MAX_IMAGE_SIZE_COMPANY_LOGO_IN_MB) {
            errorToast({ text: getLiteral('label_photo_too_big') });
            return;
        }
        if (file) {
            setNewFile(file);
            setShowCropImageDialog(true);
        }
    }, []);

    const onCropperAccept = useCallback(
        (dataUrl) => {
            onChange && onChange(field.id.toLowerCase(), dataUrl.split(/,(.+)/)[1]);
            setShowCropImageDialog(false);
        },
        [field, onChange],
    );

    const onCropperClose = useCallback(() => {
        setShowCropImageDialog(false);
    }, []);

    const onRemoveImage = useCallback(
        (event) => {
            event.stopPropagation();
            event.preventDefault();
            onChange && onChange(field.id.toLowerCase(), '');
        },
        [field, onChange],
    );

    return (
        <div className="fm-company-logo">
            <Spacer y={2} />
            <div className="fm-company-logo__header">
                <Text type="caption" className="fm-company-logo__title">
                    {title}
                </Text>

                {tooltip && (
                    <Tooltip placement="top" content={getLiteral(tooltip)}>
                        <span>
                            <Icon name="infoOutlined" size="smallMedium" />
                        </span>
                    </Tooltip>
                )}
            </div>
            <Spacer y={2} />
            <Logo type={type} onChange={onNewImage} onRemoveImage={onRemoveImage} src={src} />
            <Spacer y={2} />
            <Text type="caption">{description}</Text>
            {showCropImageDialog && (
                <CropImageDialog
                    onClose={onCropperClose}
                    onAccept={onCropperAccept}
                    show={showCropImageDialog}
                    image={newFile}
                    cropperSize={type?.width}
                    type={type}
                />
            )}
        </div>
    );
});

CompanyLogo.propTypes = propTypes;
export default CompanyLogo;
